import { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import Loader from "../../../../components/loader/Loader";
import PrimeBadge from "../../../../components/primeBadge/PrimeBadge";
import SettingsPagesContainer from "../../../../components/settingsPagesContainer/SettingsPagesContainer";
import {
  fetchAppAdmins,
  setUserAppAdminStatus,
} from "../../../../features/appAdmins/appAdminsSlice";
import { showModal } from "../../../../features/statusModal/statusModal";
import { fetchUsersByQueryAPI } from "../../../../features/userSearch/userSearchAPI";
import styles from "./appAdmins.module.css";
import UserAvatar from "../../../../components/userAvatar/UserAvatar";

const getEntityId = (item) => String(item?._id || item?.id || "");
const getGameByName = (gameIds = [], name) =>
  (Array.isArray(gameIds) ? gameIds : []).find(
    (entry) => String(entry?.gameName || "").toLowerCase() === String(name),
  );
const formatGameIdentity = (entry) => {
  if (!entry) return "غير متوفر";
  const gameName = entry?.inGameName ? String(entry.inGameName) : "غير متوفر";
  const gameId = entry?.inGameId ? String(entry.inGameId) : "غير متوفر";
  return `${gameName} - ${gameId}`;
};

const AppAdmins = () => {
  const dispatch = useDispatch();
  const { items, total, fetchStatus, updateStatus } = useSelector(
    (state) => state.appAdmins,
  );

  const [listQuery, setListQuery] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [searchStatus, setSearchStatus] = useState("idle");
  const [searchResults, setSearchResults] = useState([]);
  const [pendingUserId, setPendingUserId] = useState("");

  const loadAppAdmins = useCallback(() => {
    dispatch(
      fetchAppAdmins({
        query: listQuery,
        page: 1,
        limit: 100,
      }),
    );
  }, [dispatch, listQuery]);

  useEffect(() => {
    loadAppAdmins();
  }, [loadAppAdmins]);

  const handleSearchUsers = useCallback(async () => {
    const query = String(searchQuery || "").trim();
    if (!query) {
      setSearchResults([]);
      return;
    }

    setSearchStatus("loading");
    try {
      const response = await fetchUsersByQueryAPI(query, "user");
      const data = Array.isArray(response?.data) ? response.data : [];
      setSearchResults(data);
      setSearchStatus("succeeded");
    } catch (error) {
      setSearchStatus("failed");
      dispatch(
        showModal({
          message: "فشل البحث عن المستخدمين",
          status: "error",
        }),
      );
    }
  }, [dispatch, searchQuery]);

  const currentAdminIds = useMemo(
    () => new Set((Array.isArray(items) ? items : []).map((item) => String(item?._id || ""))),
    [items],
  );

  const handleSetAdmin = useCallback(
    async (userId, isAppAdmin) => {
      if (!userId) return;
      setPendingUserId(String(userId));
      try {
        await dispatch(setUserAppAdminStatus({ userId, isAppAdmin })).unwrap();
        loadAppAdmins();
      } finally {
        setPendingUserId("");
      }
    },
    [dispatch, loadAppAdmins],
  );

  const renderUserIdentity = useCallback((user) => {
    const ffGame = getGameByName(user?.gameIds, "freefire");
    const pubgGame = getGameByName(user?.gameIds, "pubg");
    const squadLabel =
      user?.hasSquad && user?.squad
        ? `${user?.squad?.name || ""} ${user?.squad?.slug ? `@${user.squad.slug}` : ""}`.trim()
        : "بدون سكواد";

    return (
      <>
        <span className={styles.itemTitle}>
          {user?.name || "لاعب غير معروف"} <PrimeBadge isPrime={user?.isPrime} primeTier={user?.primeTier} size="small" />{" "}
          {user?.email ? `(${user.email})` : ""}
        </span>
        <span className={styles.itemMeta}>اسم المستخدم: {user?.name || "غير متوفر"}</span>
        <span className={styles.itemMeta}>السكواد: {squadLabel}</span>
        <span className={styles.itemMeta}>Free Fire: {formatGameIdentity(ffGame)}</span>
        <span className={styles.itemMeta}>PUBG: {formatGameIdentity(pubgGame)}</span>
      </>
    );
  }, []);

  return (
    <SettingsPagesContainer title="إدارة أدمن التطبيق" fullWidth>
      <div className={styles.container}>
        <div className={styles.section}>
          <div className={styles.sectionHeader}>الأدمن الحاليون داخل التطبيق</div>
          <div className={styles.row}>
            <input
              value={listQuery}
              onChange={(event) => setListQuery(event.target.value)}
              placeholder="ابحث داخل قائمة الأدمن بالاسم أو البريد"
              className={styles.input}
            />
            <button type="button" className={styles.primaryBtn} onClick={loadAppAdmins}>
              تطبيق
            </button>
          </div>
          <div className={styles.metaRow}>
            <span>إجمالي الأدمن: {total}</span>
          </div>

          {fetchStatus === "loading" ? (
            <div className={styles.loaderContainer}>
              <Loader />
            </div>
          ) : items.length === 0 ? (
            <div className={styles.emptyState}>لا يوجد أدمن داخل التطبيق.</div>
          ) : (
            <div className={styles.list}>
              {items.map((user) => {
                const userId = getEntityId(user);
                const avatarPath = user?.userImage?.path || "";
                return (
                  <div key={userId} className={styles.listItem}>
                    <div className={styles.entityMain}>
                      <div className={styles.itemText}>
                        {renderUserIdentity(user)}
                        <span className={styles.itemMeta}>
                          تاريخ التعيين:{" "}
                          {user?.appAdminAssignedAt
                            ? new Date(user.appAdminAssignedAt).toLocaleString("ar-EG")
                            : "غير متوفر"}
                        </span>
                        <span className={styles.itemMeta}>
                          بواسطة: {user?.appAdminAssignedBy?.name || "غير متوفر"}
                        </span>
                      </div>
                      <div className={styles.entityAvatar}>
                        <UserAvatar src={avatarPath} alt="user-avatar" />
                      </div>
                    </div>

                    <button
                      type="button"
                      className={styles.dangerBtn}
                      disabled={updateStatus === "loading" && pendingUserId === userId}
                      onClick={() => handleSetAdmin(userId, false)}
                    >
                      {updateStatus === "loading" && pendingUserId === userId
                        ? "جاري الإزالة..."
                        : "إزالة الصلاحية"}
                    </button>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        <div className={styles.section}>
          <div className={styles.sectionHeader}>إضافة أدمن جديد داخل التطبيق</div>
          <div className={styles.row}>
            <input
              value={searchQuery}
              onChange={(event) => setSearchQuery(event.target.value)}
              placeholder="ابحث عن لاعب بالاسم أو البريد أو معرف اللعبة"
              className={styles.input}
            />
            <button type="button" className={styles.primaryBtn} onClick={handleSearchUsers}>
              {searchStatus === "loading" ? "جاري البحث..." : "بحث"}
            </button>
          </div>

          {searchStatus === "loading" ? (
            <div className={styles.loaderContainer}>
              <Loader />
            </div>
          ) : searchResults.length === 0 ? (
            <div className={styles.emptyState}>ستظهر نتائج البحث هنا.</div>
          ) : (
            <div className={styles.list}>
              {searchResults.map((user) => {
                const userId = getEntityId(user);
                const avatarPath = user?.userImage?.path || "";
                const alreadyAdmin =
                  currentAdminIds.has(userId) || Boolean(user?.isAppAdmin);
                return (
                  <div key={userId} className={styles.listItem}>
                    <div className={styles.entityMain}>
                      <div className={styles.itemText}>{renderUserIdentity(user)}</div>
                      <div className={styles.entityAvatar}>
                        <UserAvatar src={avatarPath} alt="user-avatar" />
                      </div>
                    </div>

                    <button
                      type="button"
                      className={styles.primaryBtn}
                      disabled={
                        alreadyAdmin ||
                        (updateStatus === "loading" && pendingUserId === userId)
                      }
                      onClick={() => handleSetAdmin(userId, true)}
                    >
                      {alreadyAdmin
                        ? "أدمن بالفعل"
                        : updateStatus === "loading" && pendingUserId === userId
                          ? "جاري الإضافة..."
                          : "تعيين كأدمن"}
                    </button>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </SettingsPagesContainer>
  );
};

export default AppAdmins;
